import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { Card, CardBody } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { ReservationCancelModal } from '@/components/venue/ReservationCancelModal'
import { ReservationDetailSections } from '@/components/venue/ReservationDetailSections'
import { ReservationRescheduleModal } from '@/components/venue/ReservationRescheduleModal'
import { useCourts } from '@/hooks/useCourts'
import { useVenueReservationBookerContact } from '@/hooks/useVenueReservationBookerContact'
import { useVenueReservationDetail } from '@/hooks/useVenueReservationDetail'
import { isReservationCancelable } from '@/lib/venue/reservationCancelViewModel'
import { useVenueContext } from '@/routes/venueContext'

/**
 * 예약 상세(설계 §2-4(3)) — #666 (d) 상세 EP 5블록을 렌더하고, 하단 "예약 처리" 영역에서 예약 변경(#669)·
 * 예약 취소 처리(#672 백엔드 + #673 이 화면)를 연동한다.
 *
 * 🚨 타 구장 예약 id 는 `fetchReservationDetail` 계약상 404 로 오고, 아래 `detailQuery.isError` 분기가
 * 그대로 `ErrorState` 를 렌더한다 — 별도 구현 없이 기존 동작이 요구사항(타 구장 접근 차단)을 만족한다.
 */
export function VenueBookingDetailPage() {
  const { venueId } = useVenueContext()
  const params = useParams<{ reservationId: string }>()
  const reservationId = Number(params.reservationId)

  const detailQuery = useVenueReservationDetail(venueId, reservationId)
  const contactQuery = useVenueReservationBookerContact(venueId, reservationId)
  const courtsQuery = useCourts(venueId)

  const [rescheduleOpen, setRescheduleOpen] = useState(false)
  const [cancelOpen, setCancelOpen] = useState(false)

  // 🚨 예약 중인 코트가 그 사이 SUSPENDED/MAINTENANCE 로 바뀌었어도 선택지에서 사라지면 안 된다 —
  // 폼 프리필 값(현재 코트)이 `<Select>` 옵션에 없으면 빈 값으로 깨진다. 코트만 유지하고 시간만
  // 바꾸는 변경도 정상 시나리오다.
  const reservableCourts =
    courtsQuery.data?.courts
      .filter((court) => court.reservable)
      .map((court) => ({ courtId: court.courtId, name: court.name })) ?? []
  const currentCourt = detailQuery.data
    ? { courtId: detailQuery.data.reservation.courtId, name: detailQuery.data.reservation.courtName }
    : null
  const courts =
    currentCourt && !reservableCourts.some((court) => court.courtId === currentCourt.courtId)
      ? [currentCourt, ...reservableCourts]
      : reservableCourts

  return (
    <>
      <PageHeader
        title="예약 상세"
        description={
          <Link to={`/venues/${venueId}/bookings/list`} className="text-brand-500 underline">
            예약 목록으로 돌아가기
          </Link>
        }
      />

      {detailQuery.isLoading ? <LoadingState rows={4} label="예약 상세를 불러오는 중" /> : null}

      {detailQuery.isError ? (
        <ErrorState error={detailQuery.error} />
      ) : detailQuery.data ? (
        <div className="space-y-4">
          <ReservationDetailSections
            detail={detailQuery.data}
            contact={{
              data: contactQuery.data,
              isFetching: contactQuery.isFetching,
              onRequest: () => {
                void contactQuery.refetch()
              },
            }}
          />

          <Card>
            <CardBody className="space-y-3">
              <p className="text-sm text-ink-700">
                {detailQuery.data.cancellation.cancellable
                  ? '취소 가능 기한 이내입니다.'
                  : '취소 가능 기한이 지났습니다.'}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  disabled={!detailQuery.data.reschedulable}
                  onClick={() => {
                    setRescheduleOpen(true)
                  }}
                >
                  예약 변경
                </Button>
                <Button
                  variant="secondary"
                  disabled={!isReservationCancelable(detailQuery.data.reservation.status)}
                  onClick={() => {
                    setCancelOpen(true)
                  }}
                >
                  예약 취소 처리
                </Button>
              </div>
            </CardBody>
          </Card>
        </div>
      ) : null}

      {rescheduleOpen && detailQuery.data ? (
        <ReservationRescheduleModal
          venueId={venueId}
          detail={detailQuery.data}
          courts={courts}
          onClose={() => {
            setRescheduleOpen(false)
          }}
        />
      ) : null}

      {cancelOpen ? (
        <ReservationCancelModal
          venueId={venueId}
          reservationId={reservationId}
          onClose={() => {
            setCancelOpen(false)
          }}
        />
      ) : null}
    </>
  )
}
